import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Loader2,
  Save,
  X,
  Plus,
  AlertCircle,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAPIClient } from '@/hooks/useAPIClient';
import { FewShotExample, UpdateExampleRequest } from '@/types/fewShot';
import { MachineMultiSelect } from '@/components/shared/MachineMultiSelect';

const editFormSchema = z.object({
  question: z.string().min(10, 'Frage muss mindestens 10 Zeichen lang sein').max(1000, 'Frage zu lang'),
  answer: z.string().min(10, 'Antwort muss mindestens 10 Zeichen lang sein').max(3000, 'Antwort zu lang'),
  explanation: z.string().optional(),
  machines: z.array(z.string()).optional(),
  submachine_part: z.string().optional(),
  error_code: z.string().optional(),
  tags: z.array(z.string()).optional(),
});

type EditFormData = z.infer<typeof editFormSchema>;

interface ExampleEditFormProps {
  example: FewShotExample;
  onCancel: () => void;
  onSuccess?: () => void;
}

export function ExampleEditForm({ example, onCancel, onSuccess }: ExampleEditFormProps) {
  const { toast } = useToast();
  const apiClient = useAPIClient();
  const queryClient = useQueryClient();
  const [tagInput, setTagInput] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
  } = useForm<EditFormData>({
    resolver: zodResolver(editFormSchema),
    defaultValues: {
      question: example.question,
      answer: example.answer,
      explanation: example.explanation || '',
      machine_parts: example.machines || [],
      submachine_part: example.submachine_part || '',
      error_code: example.error_code || '',
      tags: example.tags || [],
    },
  });

  const currentTags = watch('tags') || [];
  const explanation = watch('explanation') || '';
  const isBadExample = explanation.trim().length > 0;

  const updateMutation = useMutation({
    mutationFn: (data: UpdateExampleRequest) =>
      apiClient.fewShot.updateExample(example.example_id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['few-shot', 'examples'] });
      queryClient.invalidateQueries({ queryKey: ['few-shot', 'stats'] });
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast({
        title: 'Aktualisieren fehlgeschlagen',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handleSave = (data: EditFormData) => {
    const isGoodExample = !data.explanation?.trim();

    const updateData: UpdateExampleRequest = {
      question: data.question.trim(),
      answer: data.answer.trim(),
      is_good_example: isGoodExample,
      explanation: data.explanation?.trim() || undefined,
      machines: data.machines?.filter(m => m.trim()) || [],
      submachine_part: data.submachine_part?.trim() || undefined,
      error_code: data.error_code?.trim() || undefined,
      tags: data.tags?.filter(tag => tag.trim()) || [],
    };

    updateMutation.mutate(updateData);
  };

  const handleCancel = () => {
    reset({
      question: example.question,
      answer: example.answer,
      explanation: example.explanation || '',
      machine_parts: example.machines || [],
      submachine_part: example.submachine_part || '',
      error_code: example.error_code || '',
      tags: example.tags || [],
    });
    onCancel();
  };

  const addTag = () => {
    if (tagInput.trim() && !currentTags.includes(tagInput.trim())) {
      setValue('tags', [...currentTags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setValue('tags', currentTags.filter(tag => tag !== tagToRemove));
  };

  const handleTagKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };

  return (
    <form onSubmit={handleSubmit(handleSave)} className="space-y-4">
      {/* Question and Answer side by side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="edit-question" className="text-sm font-medium">
            Frage <span className="text-red-500">*</span>
          </Label>
          <Textarea
            id="edit-question"
            {...register('question')}
            rows={4}
            className={errors.question ? 'border-red-500' : ''}
          />
          {errors.question && (
            <p className="text-sm text-red-500">{errors.question.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="edit-answer" className="text-sm font-medium">
            Antwort <span className="text-red-500">*</span>
          </Label>
          <Textarea
            id="edit-answer"
            {...register('answer')}
            rows={4}
            className={errors.answer ? 'border-red-500' : ''}
          />
          {errors.answer && (
            <p className="text-sm text-red-500">{errors.answer.message}</p>
          )}
        </div>
      </div>

      {/* Bad example explanation */}
      <div className="space-y-2">
        <Label htmlFor="edit-explanation" className="text-sm font-medium">
          Warum ist das ein schlechtes Beispiel? (Optional)
        </Label>
        <Textarea
          id="edit-explanation"
          {...register('explanation')}
          rows={2}
          className={`${isBadExample ? 'border-red-300 bg-red-50' : ''}`}
        />
        {isBadExample && (
          <p className="text-sm text-red-600 flex items-center space-x-1">
            <AlertCircle className="h-4 w-4" />
            <span>Wird als schlechtes Beispiel gespeichert</span>
          </p>
        )}
      </div>

      {/* Metadata */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="edit-machine-parts" className="text-sm font-medium">
            Maschinen
          </Label>
          <MachineMultiSelect
            value={watch('machines') || []}
            onChange={(value) => setValue('machines', value)}
            placeholder="Maschinen auswahlen..."
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="edit-submachine-part" className="text-sm font-medium">
            Unterkomponente
          </Label>
          <Input
            id="edit-submachine-part"
            {...register('submachine_part')}
            placeholder="z.B. Zuführungseinheit"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="edit-error-code" className="text-sm font-medium">
            Fehlercode
          </Label>
          <Input
            id="edit-error-code"
            {...register('error_code')}
            placeholder="z.B. 1152"
          />
        </div>
      </div>

      {/* Tags */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Stichworte</Label>
        <div className="flex space-x-2">
          <Input
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyPress={handleTagKeyPress}
            placeholder="Stichwort hinzufügen..."
            className="flex-1"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addTag}
            disabled={!tagInput.trim()}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>

        {currentTags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {currentTags.map((tag) => (
              <Badge key={tag} variant="secondary" className="flex items-center space-x-1">
                <span>{tag}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeTag(tag)}
                  className="h-auto p-0 hover:bg-transparent"
                >
                  <X className="h-3 w-3" />
                </Button>
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex justify-end space-x-2">
        <Button
          type="button"
          variant="outline"
          onClick={handleCancel}
          disabled={updateMutation.isPending}
        >
          Abbrechen
        </Button>
        <Button
          type="submit"
          disabled={updateMutation.isPending}
          className="bg-green-600 hover:bg-green-700"
        >
          {updateMutation.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Speichern...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              Änderungen speichern
            </>
          )}
        </Button>
      </div>
    </form>
  );
}
